import { useLayoutEffect, useRef } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CaseHistoryResponseDto } from '@bandido/shared/dtos';
import { useRememberedScroll } from '@frontend/features/common/hooks/useRememberedScroll';
import { MessageRenderer } from './components/MessageRenderer';
import { MessageGroup } from './helpers';

interface Props {
  groupedMessages: MessageGroup[];
  isFetchingNextPage: boolean;
  isLoading: boolean;
  hasNextPage: boolean;
  isError: boolean;
  onLoadOlder: () => Promise<unknown>;
  onRetry: () => void;
  isCurrentUser: (history: CaseHistoryResponseDto) => boolean;
}

export const CaseContentHistoryMobile = ({ groupedMessages, isFetchingNextPage, isLoading, hasNextPage,
  isError, onLoadOlder, onRetry, isCurrentUser }: Props) => {
  const { t } = useTranslation();
  const scrollRef = useRememberedScroll(!isLoading && (!isError || groupedMessages.length > 0), 'case-history');
  const beforeLoad = useRef<{ height: number; top: number }>();
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element || isFetchingNextPage || !beforeLoad.current) return;
    element.scrollTop = beforeLoad.current.top + element.scrollHeight - beforeLoad.current.height;
    beforeLoad.current = undefined;
  }, [groupedMessages, isFetchingNextPage, scrollRef]);
  const loadOlder = () => {
    const element = scrollRef.current;
    if (element) beforeLoad.current = { height: element.scrollHeight, top: element.scrollTop };
    void onLoadOlder().catch(() => { beforeLoad.current = undefined; });
  };

  return (
    <Box ref={scrollRef} sx={{ height: '100%', overflowY: 'auto', minWidth: 0, p: 2, overflowWrap: 'anywhere' }}>
      {isLoading ? <Stack gap={2}>{[0, 1, 2].map((key) => <Skeleton key={key} variant="rounded" height={160} />)}</Stack> : (
        <Stack gap={2} sx={{ minWidth: 0 }}>
          {isError && <Alert severity="error" action={<Button onClick={onRetry}>{t('common.refresh')}</Button>}>
            {t('cases.view.historyError')}
          </Alert>}
          {hasNextPage && <Button onClick={loadOlder} disabled={isFetchingNextPage} variant="outlined" sx={{ minHeight: 44 }}>
            {isFetchingNextPage ? <CircularProgress size={20} /> : t('cases.view.loadOlder')}
          </Button>}
          {!isError && groupedMessages.length === 0 && <Typography textAlign="center" py={4}>{t('common.noData')}</Typography>}
          {groupedMessages.map((group, index) => <MessageRenderer key={group.messages[0].id} group={group}
            lastMessageDate={index ? groupedMessages[index - 1].messages[0].createdAt : null} isCurrentUser={isCurrentUser} />)}
        </Stack>
      )}
    </Box>
  );
};
